import { getColorFromId } from "@nova/util-strings"
import { isFormFieldFocused } from "@nova/util-browser"
import classNames from "classnames"
import { selectActivatedFireId } from "features/map/control/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import {
    ClipboardEventHandler,
    FormEvent,
    FunctionComponent,
    useEffect,
    useRef,
    useState,
} from "react"
import { updateLiveChat } from "tools/events/updateLiveChat"
import { useCursorRef } from "tools/events/useCursorProvider"
import { selectUserID } from "user/profile/selectors"

const FADE_OUT_DELAY = 5000

const sanitizeInput = (input: string) => {
    return input.replaceAll("\n", "").trim().slice(-40)
}

const MapbboxCursorLiveChatInput: FunctionComponent = () => {
    const [liveChatFieldIsOpen, setLiveChatFieldIsOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    const cursorRef = useCursorRef((pos) => {
        if (pos.id !== "map") {
            setLiveChatFieldIsOpen(false)
            return
        }
        if (!containerRef.current) return

        containerRef.current.style.transform = `translate(${pos.x}px, ${pos.y}px)`
    })

    // Open live chat on /, close on Esc.
    useEffect(() => {
        const handleKeydown = (e: KeyboardEvent) => {
            if (e.key === "/" && !isFormFieldFocused(e) && cursorRef.current?.id === "map") {
                setTimeout(() => setLiveChatFieldIsOpen(true), 0)
            } else if (e.key === "Escape" && liveChatFieldIsOpen) {
                setLiveChatFieldIsOpen(false)
            }
        }

        addEventListener("keydown", handleKeydown)

        return () => {
            removeEventListener("keydown", handleKeydown)
        }
    }, [cursorRef, liveChatFieldIsOpen])

    return (
        <div className="pointer-events-none absolute z-[901]" ref={containerRef}>
            {liveChatFieldIsOpen && <LiveInput onClose={() => setLiveChatFieldIsOpen(false)} />}
        </div>
    )
}

const LiveInput = ({ onClose }: { onClose: () => void }) => {
    const userId = useAppSelector(selectUserID)
    const projectId = useAppSelector(selectActivatedFireId)
    const color = getColorFromId(userId || "1")

    const editorRef = useRef<HTMLDivElement>(null)
    const [prevMessage, setPrevMessage] = useState("")
    const [showDummyMouse, setShowDummyMouse] = useState(false)

    const [isVisible, setIsVisible] = useState(true)
    const timeoutRef = useRef<NodeJS.Timeout>(undefined)

    const [isTransitioning, setIsTransitioning] = useState(false)

    const handleMouseMove = () => {
        setShowDummyMouse(false)
    }
    // Focus the input field on mount.
    useEffect(() => {
        editorRef.current?.focus()
        addEventListener("mousemove", handleMouseMove)
        timeoutRef.current = setTimeout(() => setIsVisible(false), FADE_OUT_DELAY)
        return () => {
            removeEventListener("mousemove", handleMouseMove)
            clearTimeout(timeoutRef.current)
        }
    }, [])

    const triggerNewLine = (newText: string) => {
        setPrevMessage(newText)
        if (editorRef.current)
            setTimeout(() => editorRef.current && (editorRef.current.innerText = ""), 0)
    }

    const handleInput = (e: FormEvent<HTMLDivElement>) => {
        if (!userId || !projectId) return null

        clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => setIsVisible(false), FADE_OUT_DELAY)
        setShowDummyMouse(true)
        setIsVisible(true)

        const target = e.target as HTMLTextAreaElement
        const sanitized = sanitizeInput(target.innerText)

        if (editorRef.current && editorRef.current.innerText.length >= 40) {
            triggerNewLine(sanitized)
        }

        if (sanitized && prevMessage) {
            setIsTransitioning(true)
        }

        if (sanitized) {
            updateLiveChat({ message: sanitized, userId, projectId })
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter") {
            e.preventDefault()
            if (editorRef.current) triggerNewLine(editorRef.current.innerText)
        }
    }
    const handlePaste: ClipboardEventHandler<HTMLDivElement> = (e) => {
        if (!editorRef.current) return
        if (!userId || !projectId) return
        e.preventDefault()

        clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => setIsVisible(false), FADE_OUT_DELAY)

        const content = e.clipboardData.getData("text")
        const sanitized = sanitizeInput(content)

        if (content.length >= 40) {
            triggerNewLine(sanitized)
        }

        if (sanitized) {
            updateLiveChat({ message: sanitized, userId, projectId })
        }
    }

    if (!userId || !projectId) return null

    return (
        <div
            className={classNames(
                isVisible || "transition-opacity duration-[5s]",
                isVisible ? "opacity-100" : "opacity-0",
                "-z-40"
            )}
            onTransitionEnd={onClose}
        >
            {showDummyMouse && (
                <div className="absolute -left-1">
                    <object
                        className="h-7 w-7"
                        type="image/svg+xml"
                        data="/images/cursors/cursor.svg"
                    />
                </div>
            )}
            <div
                className="relative inline-block rounded-r-lg rounded-bl-lg px-3 py-2 shadow-dp3 min-w-[1ch] pointer-events-none text-gray-0 slide top-4 left-4"
                style={{
                    backgroundColor: color,
                }}
            >
                <div
                    className={classNames(
                        "outline-none resize-none overflow-hidden whitespace-pre-wrap break-words text-base leading-tight min-w-[1ch] w-auto h-auto transition-all ease-out transform duration-900",
                        {
                            "opacity-0 absolute -translate-y-4": isTransitioning,
                        }
                    )}
                    onTransitionEnd={(e) => {
                        e.stopPropagation()
                        setIsTransitioning(false)
                        setPrevMessage("")
                    }}
                >
                    {prevMessage}
                </div>

                <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    className="pointer-events-auto outline-none resize-none overflow-hidden whitespace-pre-wrap break-words text-base leading-tight min-w-[1ch] w-auto h-[2.5ch]"
                    onKeyDown={handleKeyDown}
                    onInput={handleInput}
                    onPaste={handlePaste}
                    onBlur={onClose}
                />
            </div>
        </div>
    )
}

export { MapbboxCursorLiveChatInput }
